/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  UnknownAction,
  ListenerEffectAPI,
  PayloadAction,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import { getQueryClient } from '@opengeoweb/api';
import { getAxiosErrorMessage, isAxiosAuthError } from '@opengeoweb/shared';
import { snackbarActions } from '@opengeoweb/snackbar';
import type { MapPresetInitialProps, CoreAppStore } from '@opengeoweb/store';
import { mapSelectors, mapActions } from '@opengeoweb/store';
import { viewPresetSelectors } from '.';
import { viewPresetActions } from './reducer';
import type { AppStore } from '../types';
import { getViewPresetActiveId } from './selectors';
import type { SaveViewPresetPayload, ViewPresetError } from './types';
import {
  PresetAction,
  ViewPresetErrorComponent,
  ViewPresetErrorType,
} from './types';
import {
  emptyViewPreset,
  getSnackbarMessage,
  toViewPresetFromTemplate,
} from './utils';
import { viewpresetQueryOptions } from '../../api/viewpreset-api/queryOptions';
import { workspaceActions } from '../workspace/reducer';
import type { WorkspaceViewType } from '../workspace/types';

const processError = (
  error: AxiosError | Error,
  component: ViewPresetErrorComponent,
  errorMessageKey: string,
): ViewPresetError => {
  const message = isAxiosError(error)
    ? getAxiosErrorMessage(error)
    : errorMessageKey;
  const errorType = isAxiosAuthError(error)
    ? ViewPresetErrorType.AUTH
    : ViewPresetErrorType.GENERIC;
  return {
    message,
    component,
    errorType,
  };
};

export const viewPresetsListener = createListenerMiddleware<AppStore>();

export const saveViewPresetEffect = async (
  { payload }: PayloadAction<SaveViewPresetPayload>,
  listenerApi: ListenerEffectAPI<
    AppStore,
    ThunkDispatch<AppStore, unknown, UnknownAction>
  >,
): Promise<void> => {
  listenerApi.cancelActiveListeners();

  const { panelId, viewPreset, viewPresetId } = payload;
  const hasChanges = viewPresetSelectors.getViewPresetHasChanges(
    listenerApi.getState(),
    panelId,
  );

  if (!hasChanges) {
    listenerApi.dispatch(
      snackbarActions.openSnackbar(
        getSnackbarMessage(PresetAction.SAVE, viewPreset.title),
      ),
    );
    listenerApi.dispatch(
      viewPresetActions.savedViewPreset({ panelId, viewPresetId }),
    );
    return;
  }

  try {
    const credentials = listenerApi.getState().common?.auth?.credentials;
    await getQueryClient().fetchQuery(
      viewpresetQueryOptions.saveViewPreset(
        viewPresetId,
        viewPreset,
        credentials,
      ),
    );
    await getQueryClient().invalidateQueries(
      viewpresetQueryOptions.getViewPreset(viewPresetId),
    );
    await getQueryClient().invalidateQueries({
      queryKey: viewpresetQueryOptions.keys.lists(),
    });
    listenerApi.dispatch(
      snackbarActions.openSnackbar(
        getSnackbarMessage(PresetAction.SAVE, viewPreset.title),
      ),
    );
    listenerApi.dispatch(
      viewPresetActions.savedViewPreset({ panelId, viewPresetId }),
    );
    listenerApi.dispatch(
      viewPresetActions.fetchedViewPreset({
        panelId,
        viewPresetId,
        viewPreset,
      }),
    );
  } catch (error) {
    listenerApi.dispatch(
      viewPresetActions.errorViewPreset({
        error: processError(
          error as Error,
          ViewPresetErrorComponent.PRESET_DETAIL,
          'workspace-mappreset-error-save',
        ),
        panelId,
        viewPresetId,
      }),
    );
  }
};

// save view preset
viewPresetsListener.startListening({
  actionCreator: viewPresetActions.saveViewPreset,
  effect: saveViewPresetEffect as () => Promise<void>,
});

viewPresetsListener.startListening({
  actionCreator: viewPresetActions.selectViewPlugin,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const { panelId, pluginMetaData } = payload;
    const isMapAnimating = mapSelectors.isAnimating(
      listenerApi.getState() as CoreAppStore,
      panelId,
    );

    if (isMapAnimating) {
      listenerApi.dispatch(mapActions.mapStopAnimation({ mapId: panelId }));
    }
    if (pluginMetaData) {
      listenerApi.dispatch(
        workspaceActions.updateWorkspaceView({
          mosaicNodeId: panelId,
          viewPreset: {
            componentType: 'Plugin',
            title: pluginMetaData.version
              ? `${pluginMetaData.name} (${pluginMetaData.version})`
              : pluginMetaData.name,
            initialProps: {
              id: pluginMetaData?.id,
              version: pluginMetaData?.version,
            },
          } as WorkspaceViewType,
        }),
      );
      listenerApi.dispatch(
        viewPresetActions.setViewPresetHasChanges({
          panelId,
          hasChanges: true,
        }),
      );
    }
  },
});

viewPresetsListener.startListening({
  actionCreator: viewPresetActions.selectViewTemplate,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { panelId, template } = payload;
    const isMapAnimating = mapSelectors.isAnimating(
      listenerApi.getState() as CoreAppStore,
      panelId,
    );

    if (isMapAnimating) {
      listenerApi.dispatch(mapActions.mapStopAnimation({ mapId: panelId }));
    }

    const viewPreset = toViewPresetFromTemplate(template);

    // A map keeps its state in the store, so it needs to be reset explicitly,
    // the other modules pick up the initialProps of the new view themselves
    if (viewPreset.componentType === 'Map') {
      listenerApi.dispatch(
        mapActions.setMapPreset({
          mapId: panelId,
          initialProps: viewPreset.initialProps as MapPresetInitialProps,
        }),
      );
    }

    listenerApi.dispatch(
      workspaceActions.updateWorkspaceView({
        mosaicNodeId: panelId,
        viewPreset: viewPreset as WorkspaceViewType,
      }),
    );

    listenerApi.dispatch(
      viewPresetActions.fetchedViewPreset({
        panelId,
        viewPresetId: '',
        viewPreset,
      }),
    );
  },
});

// select view preset
viewPresetsListener.startListening({
  actionCreator: viewPresetActions.selectViewPreset,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { panelId, viewPresetId = '' } = payload;
    const isMapAnimating = mapSelectors.isAnimating(
      listenerApi.getState() as CoreAppStore,
      panelId,
    );

    if (isMapAnimating) {
      listenerApi.dispatch(mapActions.mapStopAnimation({ mapId: panelId }));
    }

    if (viewPresetId !== '') {
      listenerApi.dispatch(
        workspaceActions.fetchWorkspaceViewPreset({
          mosaicNodeId: panelId,
          viewPresetId,
        }),
      );
    } else {
      // new map preset selected
      listenerApi.dispatch(
        mapActions.setMapPreset({
          mapId: panelId,
          initialProps: emptyViewPreset('Map')
            .initialProps as MapPresetInitialProps,
        }),
      );

      // update workspace with new empy view map
      listenerApi.dispatch(
        workspaceActions.updateWorkspaceView({
          viewPreset: emptyViewPreset('Map') as WorkspaceViewType,
          mosaicNodeId: panelId,
        }),
      );
      listenerApi.dispatch(
        viewPresetActions.fetchedViewPreset({
          panelId,
          viewPresetId,
          viewPreset: emptyViewPreset('Map'),
        }),
      );
    }
  },
});

// success view preset
viewPresetsListener.startListening({
  actionCreator: viewPresetActions.onSuccessViewPresetAction,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { action, viewPresetId, title, panelId } = payload;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const is_shared = 'is_shared' in payload ? payload.is_shared : undefined;

    await getQueryClient().invalidateQueries(
      viewpresetQueryOptions.getViewPreset(viewPresetId),
    );
    await getQueryClient().invalidateQueries({
      queryKey: viewpresetQueryOptions.keys.lists(),
    });

    try {
      listenerApi.dispatch(
        snackbarActions.openSnackbar(
          getSnackbarMessage(action, title, is_shared),
        ),
      );

      const activeMapPresetId = getViewPresetActiveId(
        listenerApi.getState(),
        panelId,
      );

      let hasChanges = false;

      // if active preset has been deleted, select the empty new preset
      if (
        action === PresetAction.DELETE &&
        viewPresetId === activeMapPresetId
      ) {
        listenerApi.dispatch(
          viewPresetActions.setActiveViewPresetId({
            panelId,
            viewPresetId: '',
          }),
        );

        hasChanges = true;
      }
      if (action !== PresetAction.EDIT) {
        listenerApi.dispatch(
          viewPresetActions.setViewPresetHasChanges({
            panelId,
            hasChanges,
          }),
        );
      }

      if (
        action === PresetAction.SAVE_AS &&
        viewPresetId &&
        payload.shouldSetActiveViewPreset !== false
      ) {
        listenerApi.dispatch(
          viewPresetActions.setActiveViewPresetId({
            panelId,
            viewPresetId,
          }),
        );
      }
    } catch (_error) {
      // TODO: use error handling of mapPreset https://gitlab.com/opengeoweb/opengeoweb/-/issues/3043
    }
  },
});
